import React, { useEffect, useState } from "react";
import { type CardLayout } from "./types";
import { FilterProvider, type FilterProviderRef, type FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import CardList from "components/Tables/CardList";
import { type AxiosPromise, type AxiosResponse } from "axios";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";

export interface SelectableListProps<TInstruction, TItem, TFilter> {
  perPage?: number
  defaultFilter: TFilter
  initialInstruction: TInstruction
  excludeKeys: number[]
  excludeKeysName: string
  getId: (item: TItem) => number
  layout: CardLayout<TItem>
  filters: JSX.Element
  fetchFunction: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<TItem[], TFilter>>
  mapFilterValuesToInstruction: (values: FilterValues, page: number, perPage: number) => TInstruction
  mapResponseToFilterValues: (response: TFilter, values: FilterValues) => Partial<FilterValues>
  onSelect: (item: TItem) => void
}

const SelectableList = <
  TInstruction extends server.dto.IPaginationParameters,
  TItem,
  TFilter
>(props: SelectableListProps<TInstruction, TItem, TFilter>) => {
  const {
    perPage = 20,
    defaultFilter,
    initialInstruction,
    excludeKeys,
    excludeKeysName,
    fetchFunction,
    getId,
    layout,
    filters,
    onSelect
  } = props;
  const filterRef = React.useRef<FilterProviderRef>(null);

  const initInstruction = (instruction: TInstruction) => ({
    ...instruction,
    page: 1,
    perPage
  });

  const [instruction, setInstruction] = useState(initInstruction(initialInstruction));
  const [initialLoading, setInitialLoading] = useState(true);

  const api = useCustomQuery({
    queryFn: async () => fetchFunction(instruction),
    queryKey: [instruction],
    onSuccess: (response: AxiosResponse<server.dto.ApiResponseWrapperWithRequest<TItem[], TFilter>>) => {
      pagination.setPagination(response.data.pagination);
      if (initialLoading) {
        filterRef.current.setDefaultValues((prev: FilterValues) => {
          return props.mapResponseToFilterValues(defaultFilter, prev);
        });
      }
      setInitialLoading(false);
      filterRef.current.updateValues((prev: FilterValues) => {
        return props.mapResponseToFilterValues(response.data.request, prev);
      });
    },
    cacheTime: 0
  });

  const onFilterChange = (filter: FilterValues) => {
    setInstruction(props.mapFilterValuesToInstruction(filter, 1, perPage));
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, perPage, paginate);

  const isPaginationVisible = pagination.pageCount > 1;

  useEffect(() => {
    setInstruction({ ...instruction, [excludeKeysName]: excludeKeys });
  }, [excludeKeys]);

  return <div className="margin-right-large grow1 flex-column gap-larger">
    <FilterProvider ref={filterRef} onFilterChange={onFilterChange}>
      <FilterHeader.Filters compact isLoading={initialLoading}>
        <div className="selectiontool-header-filters">
          {filters}
        </div>
      </FilterHeader.Filters>

      <CardList
        items={api.data?.data.data ?? []}
        itemsClassName="scrollbox-auto"
        isLoading={api.isLoading}
        sections={layout.sections}
        getId={getId}
        pagination={isPaginationVisible && pagination.Component}
        hideBottomPagination
        onSelect={onSelect}
      />
    </FilterProvider>
  </div>;
};

export default SelectableList;